/**
 * Deterministic PRNG. `shared/` and the server simulation never touch
 * Math.random; every source of randomness is a `() => number` created here
 * (CONVENTIONS "shared/ is deterministic", DESIGN 5.4 `world.rng`).
 */

export type Rng = () => number;

/**
 * mulberry32: tiny 32-bit generator with a good enough period for gameplay.
 * Returns floats in [0, 1). Same seed -> same sequence on every platform
 * (only integer ops and one division).
 */
export function mulberry32(seed: number): Rng {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Uniform float in [a, b). */
export function randRange(rng: Rng, a: number, b: number): number {
  return a + (b - a) * rng();
}

/** Uniform integer in [0, n). `n <= 0` yields 0. */
export function randInt(rng: Rng, n: number): number {
  if (n <= 0) return 0;
  return Math.floor(rng() * n);
}

/** Uniform pick from a non-empty array. Throws on an empty array (programmer error). */
export function pick<T>(rng: Rng, arr: readonly T[]): T {
  if (arr.length === 0) throw new Error('pick: empty array');
  return arr[randInt(rng, arr.length)] as T;
}
